<template>
  <div class="flex flex-col gap-6">
    <!-- Loading State -->
    <div v-if="loading" class="flex flex-col items-center justify-center p-12 gap-2 text-gray-500">
      <theme-icon icon="loading" type="icons" classes="w-8 h-8 animate-spin" />
      <p>Loading permissions...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="flex items-center gap-3 p-4 bg-red-50 border-l-4 border-red-500 rounded text-red-600">
      <theme-icon icon="alert-circle" type="icons" classes="w-5 h-5" />
      <p>{{ error }}</p>
    </div>

    <!-- No Modules Found -->
    <themed-card v-else-if="filteredModules.length === 0" class="p-8 text-center text-gray-500">
      <p>No modules found.</p>
    </themed-card>

    <!-- Global Permissions Section -->
    <themed-card v-if="!loading && globalPermissionTypes.length > 0" class="mb-6 pt-6">
      <div class="mb-4">
        <h3 class="text-lg font-medium m-0 mb-1">Global Permissions</h3>
        <p class="text-sm text-gray-600 m-0">These permissions apply across all modules</p>
      </div>
      <div class="flex flex-wrap gap-4">
        <themed-checkbox
          v-for="permission in globalPermissionTypes"
          :key="permission.id"
          :id="`global-${permission.name}`"
          :label="permission.display_name || permission.name"
          :checked="hasGlobalPermission(permission.name)"
          @change="updateGlobalPermission(permission.name, $event)"
          :disabled="saving"
        />
      </div>
    </themed-card>

    <!-- Module-Specific Permissions Table -->
    <themed-card v-if="!loading && !error" class="overflow-hidden pt-6">

      <div class="mb-4">
        <h3 class="text-lg font-medium m-0 mb-1">Module Permissions</h3>
        <p class="text-sm text-gray-600 m-0">These permissions apply across all modules in Menus</p>
      </div>

        <div class="flex gap-3 items-center justify-end mb-4 flex-wrap">
          <themed-search
            v-model="searchQuery"
            placeholder="Search modules..."
            class="min-w-[250px] max-w-[250px]"
          />
        </div>

      <themed-table
        :columns="tableColumns"
        :data="filteredModules"
        :loading="loading"
        empty-message="No permission types found"
        :pagination="true"
        :per-page="perPage"
      >
        <template #column-name="{ row }">
          <div class="flex flex-col gap-1">
            <div class="font-medium">{{ row.display_name || row.slug }}</div>
            <div v-if="row.description" class="text-sm text-gray-600">{{ row.description }}</div>
          </div>
        </template>

        <template v-for="permission in permissionTypes" v-slot:[`column-${permission.name}`]="{ row }">
          <div :key="permission.id" class="flex justify-center items-center">
            <themed-checkbox
              :checked="hasPermission(row.id, permission.name)"
              @change="updatePermission(row, permission.name, $event)"
            />
          </div>
        </template>
      </themed-table>
    </themed-card>
  </div>
</template>

<script>
import { debounce } from 'lodash';
import ThemedCard from '../../ThemedCard.vue';
import ThemedSearch from '../../ThemedSearch.vue';
import ThemedCheckbox from '../../ThemedCheckbox.vue';
import ThemedTable from '../../ThemedTable.vue';
import ThemeIcon from '../../ThemeIcon.vue';

export default {
  name: 'ModulePermissions',

  components: {
    ThemedCard,
    ThemedSearch,
    ThemedCheckbox,
    ThemedTable,
    ThemeIcon
  },

  props: {
    userId: {
      type: [String, Number],
      required: true
    },
    permissionGroup: {
      type: String,
      required: true,
      default: 'module'
    },
    clientId: {
      type: [Number, String],
      required: false,
      default: null
    }
  },

  data() {
    return {
      searchQuery: '',
      loading: false,
      saving: false,
      error: null,
      modules: [],
      permissionTypes: [],
      userPermissions: {},
      globalPermissions: [],
      debouncedSearch: null,
      originalPermissions: {}, // Track original state for change detection
      hasChanges: false,
      perPage: 10
    };
  },

  computed: {
    filteredModules() {
      if (!Array.isArray(this.modules)) {
        console.error('modules is not an array:', this.modules);
        return [];
      }

      const query = (this.searchQuery || '').toLowerCase().trim();
      if (!query) return this.modules;

      return this.modules.filter(module => {
        if (!module) return false;
        const name = (module.display_name || module.slug || '').toLowerCase();
        const description = (module.description || '').toLowerCase();
        return name.includes(query) || description.includes(query);
      });
    },

    globalPermissionTypes() {
      return this.permissionTypes.filter(pt => pt.is_global === true);
    },

    entityLevelPermissionTypes() {
      return this.permissionTypes.filter(pt => pt.is_global === false);
    },

    tableColumns() {
      const columns = [
        { field: 'name', label: 'Module', sortable: true }
      ];

      this.permissionTypes.forEach(permission => {
        columns.push({
          field: permission.name,
          label: permission.display_name || permission.name,
          sortable: false,
          align: 'center'
        });
      });

      return columns;
    }
  },

  watch: {
    searchQuery() {
      this.debouncedSearch();
    },
    userId: {
      immediate: true,
      handler() {
        this.fetchData();
      }
    }
  },

  created() {
    this.debouncedSearch = debounce(this.fetchData, 300);
    this.fetchData();
    
    // Listen for save events from parent
    this.$eventHub.$on('save-user-module-permissions', this.saveAllPermissions);
    this.$eventHub.$on('save-all-permissions', this.saveAllPermissions);
  },

  beforeDestroy() {
    this.$eventHub.$off('save-user-module-permissions', this.saveAllPermissions);
    this.$eventHub.$off('save-all-permissions', this.saveAllPermissions);
  },

  methods: {
    hasPermission(moduleId, permissionName) {
      return this.userPermissions[moduleId]?.includes(permissionName) || false;
    },

    hasGlobalPermission(permissionName) {
      return this.globalPermissions.includes(permissionName);
    },

    updateGlobalPermission(permissionName, hasPermission) {
      if (hasPermission) {
        if (!this.globalPermissions.includes(permissionName)) {
          this.globalPermissions.push(permissionName);
        }
      } else {
        const index = this.globalPermissions.indexOf(permissionName);
        if (index > -1) {
          this.globalPermissions.splice(index, 1);
        }
      }
      
      this.checkForChanges();
    },

    async fetchData() {
      this.loading = true;
      this.error = null;

      try {
        const [modulesRes, permissionsRes, typesRes] = await Promise.all([
          axios.get('/api/user/accessible-modules'),
          this.userId ? axios.get(`/api/users/${this.userId}/permissions/${this.permissionGroup}`) : Promise.resolve({ data: { data: [] } }),
          axios.get('/api/permission-types', { params: { group: this.permissionGroup } })
        ]);

        this.modules = (modulesRes.data?.data || []).map(item => item.module);
        this.permissionTypes = Array.isArray(typesRes.data) ? typesRes.data : (typesRes.data?.data || []);

        const responseData = permissionsRes.data?.data || {};
        const items = responseData.items || [];
        const groupData = responseData.group || {};

        this.userPermissions = {};
        this.globalPermissions = [];

        items.forEach(item => {
          if (item.is_global && item.permissions) {
            Object.entries(item.permissions).forEach(([permName, isGranted]) => {
              if (isGranted && !this.globalPermissions.includes(permName)) {
                this.globalPermissions.push(permName);
              }
            });
          } else if (item.id && item.permissions) {
            const modulePermissions = [];
            const permissionTypes = groupData.permission_types || [];

            Object.entries(item.permissions).forEach(([permName, isGranted]) => {
              if (isGranted) {
                const isValidPermission = permissionTypes.some(pt => {
                  const typeName = typeof pt === 'object' ? pt.name : pt;
                  return typeName === permName;
                });

                if (isValidPermission) {
                  modulePermissions.push(permName);
                }
              }
            });

            this.$set(this.userPermissions, item.id, modulePermissions);
          }
        });
        
        // Save original state for change tracking
        this.saveOriginalState();

      } catch (error) {
        console.error('Failed to fetch data:', error);
        this.error = 'Failed to load permissions. Please try again later.';
      } finally {
        this.loading = false;
      }
    },

    updatePermission(module, permissionName, hasPermission) {
      if (!this.userPermissions[module.id]) {
        this.$set(this.userPermissions, module.id, []);
      }

      if (hasPermission) {
        if (!this.userPermissions[module.id].includes(permissionName)) {
          this.userPermissions[module.id].push(permissionName);
        }
      } else {
        this.userPermissions[module.id] = this.userPermissions[module.id].filter(
          p => p !== permissionName
        );
      }
      
      this.checkForChanges();
    },
    
    checkForChanges() {
      const currentState = JSON.stringify({
        user: this.userPermissions,
        global: this.globalPermissions
      });
      const originalState = JSON.stringify(this.originalPermissions);
      this.hasChanges = currentState !== originalState;
    },
    
    saveOriginalState() {
      this.originalPermissions = {
        user: JSON.parse(JSON.stringify(this.userPermissions)),
        global: [...this.globalPermissions]
      };
      this.hasChanges = false;
    },

    async saveAllPermissions() {
      this.saving = true;
      this.error = null;

      try {
        const promises = [];
        const entityTypes = this.entityLevelPermissionTypes;
        const entityTypeNames = entityTypes.map(pt => pt.name);

        // Only save permissions for modules that have changed
        this.modules.forEach((mod) => {
          if (!mod || typeof mod.id === 'undefined') return;
          
          const current = this.userPermissions[mod.id] || [];
          const original = this.originalPermissions.user[mod.id] || [];
          const currentSorted = [...current].sort();
          const originalSorted = [...original].sort();
          const hasChanged = JSON.stringify(currentSorted) !== JSON.stringify(originalSorted);
          
          if (hasChanged) {
            const payload = {};
            entityTypeNames.forEach((name) => {
              payload[name] = current.includes(name);
            });
            promises.push(
              axios.post(`/api/users/${this.userId}/permissions`, {
                permissionable_type: this.permissionGroup,
                permissionable_id: mod.id,
                permissions: payload
              })
            );
          }
        });

        // Check if global permissions changed
        if (this.globalPermissionTypes.length > 0) {
          const currentGlobalSorted = [...this.globalPermissions].sort();
          const originalGlobalSorted = [...this.originalPermissions.global].sort();
          const globalChanged = JSON.stringify(currentGlobalSorted) !== JSON.stringify(originalGlobalSorted);
          
          if (globalChanged) {
            const globalPayload = {};
            this.globalPermissionTypes.forEach(pt => {
              globalPayload[pt.name] = this.globalPermissions.includes(pt.name);
            });
            promises.push(
              axios.post(`/api/users/${this.userId}/permissions`, {
                permissionable_type: this.permissionGroup,
                permissionable_id: null,
                permissions: globalPayload
              })
            );
          }
        }

        if (promises.length === 0) {
          this.saving = false;
          this.$eventHub.$emit('permission-saved', { section: 'Module Permissions', hasChanges: false });
          return;
        }

        await Promise.all(promises);
        
        // Update original state after successful save
        this.saveOriginalState();
        
        this.$eventHub.$emit('permission-saved', { section: 'Module Permissions', hasChanges: true });

      } catch (error) {
        console.error('Error saving module permissions:', error);
        this.error = 'Failed to save module permissions. Please try again.';
        
        this.$eventHub.$emit('permission-save-error');
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>
